import { createSlice } from "@reduxjs/toolkit"
import {
  createTrialOperation,
  updateTrialOperation,
  deleteTrialOperation,
  trialOperationInviteCandidate,
  getAllTrialPeriods,
  getCateringList,
  getTrialPeriodMatchedCandidatesList,
  getTrialPeriodSchedule,
  markTrialPeriodAttendance,
  updateSettings,
  trialOperationInviteCandidateViaFile,
  getTrialOperationDetails,
  getAllTrialOperationsAttendanceList,
  getAllTrialOperationsEnrollmentList,
  editTrialPeriodAttendance,
  getAllTrialOperationsReasonList,
  trialOperationCaptureAbsent,
  getAllTrialOperationsInspectionList,
  deleteTrialOperationInspection,
  markTrialOperationInspectionAttendance
} from "store/actions/trialPeriodAction"

const initialPayload = {
  limit: 50,
  page_number: 1,
  order_by: 1,
  filters: {
    keyword: "",
    date_range: [],
  },
}

const cateringInitialPayload = {
  limit: 50,
  page_number: 1,
  order_by: -1,
  trial_run_schedule_uuid: null,
  filters: {
    is_trial_run: true,
    keyword: "",
    date_range: [],
    type: 0,
    attendance_type: 0,
  },
}

const attendanceInitialPayload = {
  limit: 50,
  page_number: 1,
  order_by: -1,
  filters: {
    is_trial_run: true,
    keyword: "",
    date_range: [],
    shift:"",
    station: null,
    attendance_admin_id: "",
    job_title_ids: [],
    attendance_type: 0
  },
}

const inspectionInitialPayload = {
  limit: 50,
  page_number: 1,
  order_by: -1,
  filters: {
    is_trial_run: true,
    keyword: "",
    date_range: [],
    shift:"",
    station: null,
    attendance_admin_id: "",
    job_title_ids: [],
    type: 0
  },
}

const enrollmentInitialPayload = {
  limit: 50,
  page_number: 1,
  order_by: -1,
  filters: {
    is_trial_run: true,
    keyword: "",
    date_range: [],
    type: 0,
    job_title_ids: [],
  },
}

const candidatesInitialPayload = {
  limit: 50,
  page_number: 1,
  order_by: -1,
  filters: {
    keyword: "",
    date_range: [],
    type: 0,
  },
}

const initialState = {
  loading: false,
  loading2: false,
  isLoading: null,
  perPage: 50,
  totalPages: null,
  currentPage: null,
  totalCount: null,
  trialPeriods: [],
  matchedCandidatesList: [],
  payload: initialPayload,
  allowTrialOperationSchedule: false,
  activeTab: "trial_operation",
  candidatesActiveTab: 0,
  isShowFilter: false,
  cateringPayload: cateringInitialPayload,
  caterings: [],
  trialPeriodSchedules: [],
  isGettingDetails: false,
  trialOperationDetails: {},
  trialOperationsAttendance: [],
  trialOperationsEnrollments: [],
  trialOperationsReasonList: [],
  trialOperationsInspectionList: [],
  attendancePayload: attendanceInitialPayload,
  enrollmentPayload: enrollmentInitialPayload,
  candidatesPayload: candidatesInitialPayload,
  inspectionPayload: inspectionInitialPayload,
}

const trialPeriodSlice = createSlice({
  name: "Trial Operation",
  initialState,
  reducers: {
    setPayload: (state, action) => {
      state.payload = { ...state.payload, ...action.payload }
    },

    setCateringPayload: (state, action) => {
      state.cateringPayload = { ...state.cateringPayload, ...action.payload }
    },
    resetCateringPayload: state => {
      state.cateringPayload = cateringInitialPayload
    },
    setAttendancePayload: (state, action) => {
      state.attendancePayload = {
        ...state.attendancePayload,
        ...action.payload,
      }
    },
    resetAttendancePayload: state => {
      state.attendancePayload = attendanceInitialPayload
    },
    setInspectionPayload: (state, action) => {
      state.inspectionPayload = {
        ...state.inspectionPayload,
        ...action.payload,
      }
    },
    resetInspectionPayload: state => {
      state.inspectionPayload = inspectionInitialPayload
    },
    setEnrollmentPayload: (state, action) => {
      state.enrollmentPayload = {
        ...state.enrollmentPayload,
        ...action.payload,
      }
    },
    resetEnrollmentPayload: state => {
      state.enrollmentPayload = enrollmentInitialPayload
    },
    setCandidatesPayload: (state, action) => {
      state.candidatesPayload = {
        ...state.candidatesPayload,
        ...action.payload,
      }
    },
    resetCandidatesPayload: state => {
      state.candidatesPayload = enrollmentInitialPayload
    },
    resetPayload: state => {
      state.payload = initialPayload
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload
    },
    setCandidatesActiveTab: (state, action) => {
      state.candidatesActiveTab = action.payload
    },
    toggleFilter: state => {
      state.isShowFilter = !state.isShowFilter
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getAllTrialPeriods.pending, state => {
        state.loading = true
        state.totalPages = null
        state.totalCount = null
        state.currentPage = null
      })
      .addCase(getAllTrialPeriods.fulfilled, (state, action) => {
        state.loading = false
        const { success, data, settings } = action.payload || {}
        if (success) {
          const { total_records, current_page, total_pages } =
            action.payload || {}
          state.trialPeriods = data
          state.totalPages = total_pages
          state.totalCount = total_records
          state.currentPage = current_page
          state.allowTrialOperationSchedule =
            settings.trial_operation_allow_schedule
        }
      })
      .addCase(getAllTrialPeriods.rejected, (state, action) => {
        state.loading = false
      })

    builder
      .addCase(getTrialOperationDetails.pending, state => {
        state.isGettingDetails = true
        state.trialOperationDetails = {}
      })
      .addCase(getTrialOperationDetails.fulfilled, (state, action) => {
        state.isGettingDetails = false
        const { success, data } = action.payload || {}
        if (success) {
          state.trialOperationDetails = data
        }
      })
      .addCase(getTrialOperationDetails.rejected, (state, action) => {
        state.isGettingDetails = false
      })

    builder
      .addCase(createTrialOperation.pending, state => {
        state.isLoading = true
      })
      .addCase(createTrialOperation.fulfilled, (state, action) => {
        state.isLoading = false
      })
      .addCase(createTrialOperation.rejected, (state, action) => {
        state.isLoading = false
      })

    builder
      .addCase(updateTrialOperation.pending, state => {
        state.isLoading = true
      })
      .addCase(updateTrialOperation.fulfilled, (state, action) => {
        state.isLoading = false
      })
      .addCase(updateTrialOperation.rejected, (state, action) => {
        state.isLoading = false
      })

    builder
      .addCase(deleteTrialOperation.pending, state => {
        state.isLoading = true
      })
      .addCase(deleteTrialOperation.fulfilled, (state, action) => {
        state.isLoading = false
      })
      .addCase(deleteTrialOperation.rejected, (state, action) => {
        state.isLoading = false
      })

    builder
      .addCase(trialOperationInviteCandidate.pending, state => {
        state.isLoading = true
      })
      .addCase(trialOperationInviteCandidate.fulfilled, (state, action) => {
        state.isLoading = false
      })
      .addCase(trialOperationInviteCandidate.rejected, (state, action) => {
        state.isLoading = false
      })

    builder
      .addCase(trialOperationInviteCandidateViaFile.pending, state => {
        state.isLoading = true
      })
      .addCase(
        trialOperationInviteCandidateViaFile.fulfilled,
        (state, action) => {
          state.isLoading = false
        }
      )
      .addCase(
        trialOperationInviteCandidateViaFile.rejected,
        (state, action) => {
          state.isLoading = false
        }
      )

    builder
      .addCase(getTrialPeriodMatchedCandidatesList.pending, state => {
        state.loading2 = true
        state.totalPages = null
        state.totalCount = null
        state.currentPage = null
      })
      .addCase(
        getTrialPeriodMatchedCandidatesList.fulfilled,
        (state, action) => {
          state.loading2 = false
          const { success, data } = action.payload || {}
          if (success) {
            const { total_records, current_page, total_pages } =
              action.payload || {}
            state.matchedCandidatesList = data
            state.totalCount = total_records
            state.totalPages = total_pages
            state.totalCount = total_records
            state.currentPage = current_page
          }
        }
      )
      .addCase(
        getTrialPeriodMatchedCandidatesList.rejected,
        (state, action) => {
          state.loading2 = false
        }
      )

    builder
      .addCase(updateSettings.pending, state => {
        state.loading2 = true
      })
      .addCase(updateSettings.fulfilled, (state, action) => {
        state.loading2 = false
        const { success } = action.payload || {}
        if (success) {
          state.allowTrialOperationSchedule = !state.allowTrialOperationSchedule
        }
      })
      .addCase(updateSettings.rejected, (state, action) => {
        state.loading2 = false
      })

    builder
      .addCase(getCateringList.pending, state => {
        state.loading = true
        state.totalPages = null
        state.totalCount = null
        state.currentPage = null
      })
      .addCase(getCateringList.fulfilled, (state, action) => {
        state.loading = false
        const { success, data } = action.payload || {}
        if (success) {
          const { total_records, current_page, total_pages } =
            action.payload || {}
          state.caterings = data
          state.totalPages = total_pages
          state.totalCount = total_records
          state.currentPage = current_page
        }
      })
      .addCase(getCateringList.rejected, (state, action) => {
        state.loading = false
      })

    builder
      .addCase(markTrialPeriodAttendance.pending, state => {})
      .addCase(markTrialPeriodAttendance.fulfilled, (state, action) => {})
      .addCase(markTrialPeriodAttendance.rejected, (state, action) => {})

    builder
      .addCase(trialOperationCaptureAbsent.pending, state => {})
      .addCase(trialOperationCaptureAbsent.fulfilled, (state, action) => {})
      .addCase(trialOperationCaptureAbsent.rejected, (state, action) => {})

    builder
      .addCase(editTrialPeriodAttendance.pending, state => {})
      .addCase(editTrialPeriodAttendance.fulfilled, (state, action) => {})
      .addCase(editTrialPeriodAttendance.rejected, (state, action) => {})

    builder
      .addCase(getAllTrialOperationsAttendanceList.pending, state => {
        state.loading = true
        state.totalPages = null
        state.totalCount = null
        state.currentPage = null
      })
      .addCase(
        getAllTrialOperationsAttendanceList.fulfilled,
        (state, action) => {
          state.loading = false
          const { success, data } = action.payload || {}
          if (success) {
            const { total_records, current_page, total_pages } =
              action.payload || {}
            state.trialOperationsAttendance = data
            state.totalPages = total_pages
            state.totalCount = total_records
            state.currentPage = current_page
          }
        }
      )
      .addCase(
        getAllTrialOperationsAttendanceList.rejected,
        (state, action) => {
          state.loading = false
        }
      )

    builder
      .addCase(getAllTrialOperationsEnrollmentList.pending, state => {
        state.loading = true
        state.totalPages = null
        state.totalCount = null
        state.currentPage = null
      })
      .addCase(
        getAllTrialOperationsEnrollmentList.fulfilled,
        (state, action) => {
          state.loading = false
          const { success, data } = action.payload || {}
          if (success) {
            const { total_records, current_page, total_pages } =
              action.payload || {}
            state.trialOperationsEnrollments = data
            state.totalPages = total_pages
            state.totalCount = total_records
            state.currentPage = current_page
          }
        }
      )
      .addCase(
        getAllTrialOperationsEnrollmentList.rejected,
        (state, action) => {
          state.loading = false
        }
      )

    builder
      .addCase(getAllTrialOperationsReasonList.pending, state => {
      })
      .addCase(
        getAllTrialOperationsReasonList.fulfilled,
        (state, action) => {
          const { success, data } = action.payload || {}
          if (success) {
            state.trialOperationsReasonList = data
          }
        }
      )
      .addCase(
        getAllTrialOperationsReasonList.rejected,
        (state, action) => {
          state.loading = false
        }
      )

    builder
      .addCase(markTrialOperationInspectionAttendance.pending, state => {})
      .addCase(markTrialOperationInspectionAttendance.fulfilled, (state, action) => {})
      .addCase(markTrialOperationInspectionAttendance.rejected, (state, action) => {})

    builder
      .addCase(getAllTrialOperationsInspectionList.pending, state => {
        state.loading = true
        state.totalPages = null
        state.totalCount = null
        state.currentPage = null
      })
      .addCase(
        getAllTrialOperationsInspectionList.fulfilled,
        (state, action) => {
          state.loading = false
          const { success, data } = action.payload || {}
          if (success) {
            const { total_records, current_page, total_pages } =
            action.payload || {}
            state.trialOperationsInspectionList = data
            state.totalPages = total_pages
            state.totalCount = total_records
            state.currentPage = current_page
          }
        }
      )
      .addCase(
        getAllTrialOperationsInspectionList.rejected,
        (state, action) => {
          state.loading = false
        }
      )

    builder
      .addCase(deleteTrialOperationInspection.pending, state => {
        state.isLoading = true
      })
      .addCase(deleteTrialOperationInspection.fulfilled, (state, action) => {
        state.isLoading = false
      })
      .addCase(deleteTrialOperationInspection.rejected, (state, action) => {
        state.isLoading = false
      })

  },
})

export const {
  setPayload,
  resetPayload,
  setActiveTab,
  setCandidatesActiveTab,
  toggleFilter,
  setCateringPayload,
  resetCateringPayload,
  setAttendancePayload,
  resetAttendancePayload,
  setInspectionPayload,
  resetInspectionPayload,
  setEnrollmentPayload,
  resetEnrollmentPayload,
  setCandidatesPayload,
  resetCandidatesPayload,
} = trialPeriodSlice.actions
export default trialPeriodSlice.reducer
